/**
 * useIstanti — cloud-only (Emergent object storage via backend).
 *
 * Requires authentication. Anonymous users see an empty state with CTA to login.
 * The IndexedDB legacy is deliberately dropped: photos MUST live in the cloud
 * so they survive cache clears and device changes.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function currentPeriod() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${d.getFullYear()}-${m}`;
}

export function imageUrlFor(istante) {
  if (!istante?.id) return null;
  return `${API}/istanti/${istante.id}/image`;
}

export function useIstanti() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setItems([]);
      setLoaded(true);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/istanti`);
      setItems(Array.isArray(data.items) ? data.items : []);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoaded(true);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (authLoading) return;
    refresh();
  }, [authLoading, isAuthenticated, refresh]);

  const uploadIstante = useCallback(
    async ({ blob, date, slot, timeHHMM }) => {
      const fd = new FormData();
      fd.append("file", blob, `istante-${date}-${slot}.jpg`);
      fd.append("date", date);
      fd.append("slot", slot);
      fd.append("time_hhmm", timeHHMM || "");
      const { data } = await axios.post(`${API}/istanti/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setItems((prev) => [data, ...prev]);
      return data;
    },
    []
  );

  const deleteIstante = useCallback(async (id) => {
    await axios.delete(`${API}/istanti/${id}`);
    setItems((prev) => prev.filter((it) => it.id !== id));
  }, []);

  // Filtra al mese corrente per la UI TIME Watch e Area Personale. Gli
  // Istanti storici restano nel cloud e vengono mostrati soltanto negli
  // Archivi mensili scaricabili.
  const period = currentPeriod();
  const itemsCurrentMonth = useMemo(
    () =>
      items.filter(
        (it) => typeof it.date === "string" && it.date.startsWith(period + "-")
      ),
    [items, period]
  );

  return {
    items: itemsCurrentMonth,
    allItems: items,
    loaded,
    error,
    isAuthenticated,
    uploadIstante,
    deleteIstante,
    refresh,
  };
}
